import React, { useEffect, useState } from 'react';
import './PickAccount.css';
import { useNavigate } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getEnvironmentParams } from '../utils/auth';
import { apiUrl } from '../config/envConfig';
import PageLayout from './common/PageLayout';
import { useTranslation } from 'react-i18next';
import { returnWithBcStart } from '../utils/returnToHost';

// HTTP status codes that imply the consent or session can't be saved — we
// can't recover by retrying, so send the user back to start a fresh consent.
const UNRECOVERABLE_STATUSES = new Set([401, 403, 410]);

const currencySymbols = {
    SEK: 'kr',
    EUR: '€',
    USD: '$',
    GBP: '£',
};

function getCurrencySymbol(currency) {
    return currencySymbols[currency] || currency;
}

function PickAccount() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [accounts, setAccounts] = useState([]);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSelectingAccount, setIsSelectingAccount] = useState(false);

    const consentData = JSON.parse(sessionStorage.getItem('consentData'));
    const bankingInfo = JSON.parse(sessionStorage.getItem('selectedBank'));
    const [userIp, setUserIp] = useState('');
    const [accountsFetched, setAccountsFetched] = useState(false);

    useEffect(() => {
        fetch('https://api.ipify.org?format=json')
            .then(res => res.json())
            .then(data => setUserIp(data.ip))
            .catch(() => setUserIp('127.0.0.1'));
    }, []);

    const fetchAccounts = async () => {
        try {
            setError(null);
            setLoading(true);
            const params = getEnvironmentParams();
            if (!params.token) {
                throw new Error('No authentication token found');
            }
            const headers = {
                'accept': 'application/json',
                'Consent-Id': consentData?.consentId,
                'BicFi': bankingInfo?.bicFi,
                'Psu-Ip-Address': userIp,
                'Psu-Id': bankingInfo?.socialSecurityNumber,
                'Authorization': `Bearer ${params.token}`,
                'Tpp-Redirect-Preferred': false
            };
            const response = await fetch(`${apiUrl}/accounts`, {
                method: 'GET',
                headers: headers
            });
            if (!response.ok) {
                if (UNRECOVERABLE_STATUSES.has(response.status)) {
                    // Token / consent is gone — restart the consent flow on the host side.
                    returnWithBcStart('/aspspsList');
                    return;
                }
                throw new Error(`HTTP ${response.status}`);
            }
            const data = await response.json();
            setAccounts(Array.isArray(data) ? data : (data.accounts || []));
        } catch (err) {
            console.error('Error loading accounts:', err);
            setError(t('pickLoadAccountsError', 'Could not load accounts. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (
            !accountsFetched &&
            consentData?.consentId &&
            bankingInfo?.bicFi &&
            userIp
        ) {
            setAccountsFetched(true);
            fetchAccounts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountsFetched, consentData, bankingInfo, userIp]);

    const handleAccountSelect = (accountIdx) => {
        setSelectedAccount(prev => prev === accountIdx ? null : accountIdx);
    };

    const handleNextClick = async () => {
        if (isSelectingAccount || selectedAccount === null) {
            return;
        }

        setIsSelectingAccount(true);
        setError(null);

        try {
            const params = getEnvironmentParams();
            if (!params.token) {
                throw new Error('No authentication token found');
            }

            const headers = {
                'accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${params.token}`,
                'Tpp-Redirect-Preferred': false,
                'Psu-Id': bankingInfo.socialSecurityNumber,
            };

            const account = accounts[selectedAccount];

            const response = await fetch(
                `${apiUrl}/consents/${consentData.consentId}/accounts/${account.id}`,
                {
                    method: 'PUT',
                    headers: headers,
                    body: JSON.stringify({
                        accountId: account.resourceId,
                        selected: true
                    })
                }
            );

            if (!response.ok) {
                if (UNRECOVERABLE_STATUSES.has(response.status)) {
                    returnWithBcStart('/aspspsList');
                    return;
                }
                throw new Error(`HTTP ${response.status}`);
            }

            sessionStorage.setItem('selectedAccountsData', JSON.stringify([account]));
            navigate('/summary');
        } catch (err) {
            console.error('Error selecting account:', err);
            setError(t('pickSelectAccountError', 'Could not save your account selection. Please try again.'));
        } finally {
            setIsSelectingAccount(false);
        }
    };

    const handleRetryFetch = () => {
        setError(null);
        setAccountsFetched(false);
    };

    const handleBackToStart = () => {
        returnWithBcStart('/aspspsList');
    };

    const renderAccountsContent = () => {
        if (loading) {
            return <div>{t('pickLoading')}</div>;
        }
        if (error && accounts.length === 0) {
            return (
                <div>
                    <div className="error-message">{error}</div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                        <button type="button" onClick={handleRetryFetch}>
                            {t('pickRetry', 'Try again')}
                        </button>
                        <button type="button" onClick={handleBackToStart}>
                            {t('pickBackToStart', 'Back to start')}
                        </button>
                    </div>
                </div>
            );
        }
        if (accounts.length === 0) {
            return <div className="no-accounts-message">{t('pickNoAccounts')}</div>;
        }
        return (
            <div className="accounts-list">
                {accounts.map((account, idx) => {
                    const isSelected = selectedAccount === idx;
                    return (
                        <div
                            key={`${account.name}-${account.balance}-${account.currency}`}
                            className={`account-item ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleAccountSelect(idx)}
                        >
                            <div className="account-info">
                                <div className={`account-radio ${isSelected ? 'checked' : ''}`}>
                                    {isSelected && <div className="account-radio-dot" />}
                                </div>
                                <div className="account-details">
                                    <span className="account-type">{account.name}</span>
                                    <span className="account-number">····{account.accountNumber || ''}</span>
                                </div>
                            </div>
                            <div className="account-balance">
                                <span>
                                    {Number(account.balance).toLocaleString()} {getCurrencySymbol(account.currency)}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const navigationProps = {
        previousPath: "/bank-connected",
        onNext: handleNextClick,
        nextDisabled: selectedAccount === null || !acceptedTerms || isSelectingAccount
    };

    return (
        <PageLayout
            currentStep={3}
            navigationProps={navigationProps}
            infoText="pickInfoText"
        >
            <div className="pick-account-content">
                <h1>{t('pickTitle')}</h1>
                <p className="subtitle">{t('pickSubtitle')}</p>

                {renderAccountsContent()}

                {error && accounts.length > 0 && (
                    <div className="error-message" style={{ marginTop: 12 }}>
                        {error}
                    </div>
                )}

                {isSelectingAccount && (
                    <div className="selecting-account-message">
                        {t('pickSelectingAccount')}
                    </div>
                )}

                <div className="terms-section">
                    <h4 className="terms-heading">{t('pickTermsHeading')}</h4>
                    <label className="terms-checkbox">
                        <input
                            type="checkbox"
                            checked={acceptedTerms}
                            onChange={(e) => setAcceptedTerms(e.target.checked)}
                        />
                        <span>
                            {t('pickAcceptPrefix')}{' '}
                            <a href="/terms-full" target="_blank">{t('pickTerms')}</a>
                            {' '}{t('pickAnd')}{' '}
                            <a href="/privacy-policy" target="_blank">{t('pickPrivacy')} <OpenInNewIcon fontSize="small" /></a>
                            {' '}{t('pickAuthorisePayments')}
                        </span>
                    </label>
                </div>
            </div>
        </PageLayout>
    );
}

export default PickAccount;
